import { config } from "./../../config";
import { useState } from "react";

const Privacy = () => {
    const { appName, contactDetails } = config;
    
    return (
        <div className="min-h-[calc(100vh-200px)] px-4 sm:px-6 lg:px-8 py-12">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="text-center mb-12">
                    <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
                    <div className="w-24 h-1 bg-blue-600 mx-auto mb-4"></div>
                    <p className="text-lg text-gray-600">
                        Last updated: {new Date().toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                        })}
                    </p>
                </div>
                
                <div className="p-8">
                    <div className="mb-10 p-6 bg-blue-50 rounded-xl border border-blue-200">
                        <p className="text-gray-700 text-lg">
                            At <span className="font-semibold text-blue-600">{appName}</span>, we are committed to protecting your privacy. 
                            This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our services.
                        </p>
                    </div>
                    
                    <div className="space-y-12">
                        {/* Information We Collect */}
                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-2 border-b border-gray-200">
                                1. Information We Collect
                            </h2>
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-800 mb-3">Personal Information</h3>
                                    <p className="text-gray-700 mb-4">
                                        We collect personal information that you voluntarily provide to us when you:
                                    </p>
                                    <ul className="list-disc pl-6 space-y-2 text-gray-700">
                                        <li>Create an account or register for our services</li>
                                        <li>Make a booking or purchase tickets</li>
                                        <li>Contact our customer support</li>
                                        <li>Participate in promotions or surveys</li>
                                        <li>Subscribe to our newsletters</li>
                                    </ul>
                                </div>
                                
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-800 mb-3">Automatically Collected Information</h3>
                                    <p className="text-gray-700 mb-3">
                                        When you use our services, we automatically collect:
                                    </p>
                                    <ul className="list-disc pl-6 space-y-2 text-gray-700">
                                        <li>Device information (IP address, browser type, operating system)</li>
                                        <li>Usage data (pages visited, time spent, click patterns)</li>
                                        <li>Location data (with your permission)</li>
                                        <li>Cookies and similar tracking technologies</li>
                                    </ul>
                                </div>
                                
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-800 mb-3">Payment Information</h3>
                                    <p className="text-gray-700">
                                        We use secure payment processors for all transactions. We do not store your complete 
                                        payment card details. Only payment tokens and transaction IDs are stored for reference.
                                    </p>
                                </div>
                            </div>
                        </section>
                        
                        {/* How We Use Your Information */}
                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-2 border-b border-gray-200">
                                2. How We Use Your Information
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="bg-gray-50 p-5 rounded-lg">
                                    <h4 className="font-semibold text-gray-800 mb-2">Service Provision</h4>
                                    <p className="text-gray-700 text-sm">
                                        Process bookings, send confirmations, provide customer support, and manage your account.
                                    </p>
                                </div>
                                <div className="bg-gray-50 p-5 rounded-lg">
                                    <h4 className="font-semibold text-gray-800 mb-2">Communication</h4>
                                    <p className="text-gray-700 text-sm">
                                        Send booking updates, service announcements, promotional offers (with consent), and respond to inquiries.
                                    </p>
                                </div>
                                <div className="bg-gray-50 p-5 rounded-lg">
                                    <h4 className="font-semibold text-gray-800 mb-2">Service Improvement</h4>
                                    <p className="text-gray-700 text-sm">
                                        Analyze usage patterns, improve our services, develop new features, and personalize your experience.
                                    </p>
                                </div>
                                <div className="bg-gray-50 p-5 rounded-lg">
                                    <h4 className="font-semibold text-gray-800 mb-2">Security & Compliance</h4>
                                    <p className="text-gray-700 text-sm">
                                        Detect and prevent fraud, comply with legal obligations, enforce our terms, and protect our rights.
                                    </p>
                                </div>
                            </div>
                        </section>
                        
                        {/* Data Sharing */}
                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-2 border-b border-gray-200">
                                3. Data Sharing and Disclosure
                            </h2>
                            <div className="space-y-4">
                                <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                                    <h4 className="font-semibold text-gray-800 mb-2">We do not sell your personal data</h4>
                                    <p className="text-gray-700">
                                        We may share your information only in the following circumstances:
                                    </p>
                                </div>
                                <ul className="list-disc pl-6 space-y-3 text-gray-700">
                                    <li>
                                        <span className="font-semibold">Service Providers:</span> With trusted partners who assist in payment processing, SMS services, email delivery, and analytics.
                                    </li>
                                    <li>
                                        <span className="font-semibold">Legal Requirements:</span> When required by law, court order, or government request.
                                    </li>
                                    <li>
                                        <span className="font-semibold">Business Transfers:</span> In connection with a merger, acquisition, or sale of assets.
                                    </li>
                                    <li>
                                        <span className="font-semibold">Safety & Security:</span> To protect our rights, property, or safety, or that of others.
                                    </li>
                                </ul>
                            </div>
                        </section>
                        
                        {/* Data Security */}
                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-2 border-b border-gray-200">
                                4. Data Security
                            </h2>
                            <div className="bg-green-50 p-6 rounded-xl border border-green-200">
                                <div className="flex items-start">
                                    <svg className="w-6 h-6 text-green-600 mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                                    </svg>
                                    <div>
                                        <h4 className="font-semibold text-gray-800 mb-2">Industry-Standard Protection</h4>
                                        <p className="text-gray-700">
                                            We implement appropriate technical and organizational security measures to protect your 
                                            personal information against unauthorized access, alteration, disclosure, or destruction. 
                                            These include encryption, access controls, secure servers, and regular security assessments.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </section>
                        
                        {/* Your Rights */}
                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-2 border-b border-gray-200">
                                5. Your Privacy Rights
                            </h2>
                            <div className="space-y-4">
                                <p className="text-gray-700">
                                    Depending on your location, you may have the following rights regarding your personal data:
                                </p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="bg-gray-50 p-4 rounded-lg">
                                        <h4 className="font-semibold text-gray-800 mb-1">Right to Access</h4>
                                        <p className="text-gray-700 text-sm">Request a copy of your personal data.</p>
                                    </div>
                                    <div className="bg-gray-50 p-4 rounded-lg">
                                        <h4 className="font-semibold text-gray-800 mb-1">Right to Correction</h4>
                                        <p className="text-gray-700 text-sm">Request correction of inaccurate data.</p>
                                    </div>
                                    <div className="bg-gray-50 p-4 rounded-lg">
                                        <h4 className="font-semibold text-gray-800 mb-1">Right to Deletion</h4>
                                        <p className="text-gray-700 text-sm">Request deletion of your personal data.</p>
                                    </div>
                                    <div className="bg-gray-50 p-4 rounded-lg">
                                        <h4 className="font-semibold text-gray-800 mb-1">Right to Object</h4>
                                        <p className="text-gray-700 text-sm">Object to processing of your personal data.</p>
                                    </div>
                                </div>
                                <p className="text-gray-700">
                                    To exercise these rights, please contact us using the information provided below.
                                </p>
                            </div>
                        </section>
                        
                        {/* Cookies */}
                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-2 border-b border-gray-200">
                                6. Cookies and Tracking Technologies
                            </h2>
                            <div className="space-y-4">
                                <p className="text-gray-700">
                                    We use cookies and similar technologies to enhance your experience, analyze usage, and personalize content. 
                                    You can control cookies through your browser settings. However, disabling cookies may affect some features of our services.
                                </p>
                                <div className="bg-blue-50 p-4 rounded-lg">
                                    <p className="text-gray-700">
                                        <span className="font-semibold">Essential Cookies:</span> Required for basic functionality
                                        <br />
                                        <span className="font-semibold">Analytics Cookies:</span> Help us understand how users interact with our services
                                        <br />
                                        <span className="font-semibold">Preferences Cookies:</span> Remember your settings and preferences
                                    </p>
                                </div>
                            </div>
                        </section>
                        
                        {/* Children's Privacy */}
                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-2 border-b border-gray-200">
                                7. Children's Privacy
                            </h2>
                            <div className="bg-red-50 p-5 rounded-xl border border-red-200">
                                <p className="text-gray-700">
                                    Our services are not intended for individuals under the age of 16. We do not knowingly collect 
                                    personal information from children. If we become aware that we have collected personal information 
                                    from a child without parental consent, we will take steps to delete that information.
                                </p>
                            </div>
                        </section>
                        
                        {/* Changes to Policy */}
                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-2 border-b border-gray-200">
                                8. Changes to This Privacy Policy
                            </h2>
                            <p className="text-gray-700">
                                We may update this Privacy Policy periodically. We will notify you of any material changes by 
                                posting the new Privacy Policy on this page and updating the "Last Updated" date. We encourage 
                                you to review this Privacy Policy regularly for any changes.
                            </p>
                        </section>
                        
                        {/* Contact Information */}
                        <section className="bg-gradient-to-r from-blue-50 to-indigo-50 p-8 rounded-xl border border-blue-200">
                            <h2 className="text-2xl font-bold text-gray-900 mb-6">Contact Us</h2>
                            <p className="text-gray-700 mb-6">
                                If you have any questions, concerns, or requests regarding this Privacy Policy or our 
                                privacy practices, please contact us:
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <h4 className="font-semibold text-gray-800 mb-2">Email</h4>
                                    <a href={`mailto:${contactDetails.email}?subject=Privacy Policy Inquiry`} 
                                       className="text-blue-600 hover:text-blue-800">
                                        {contactDetails.email}
                                    </a>
                                </div>
                                <div>
                                    <h4 className="font-semibold text-gray-800 mb-2">Phone</h4>
                                    <a href={`tel:${contactDetails.phone.replace('+', '')}`} 
                                       className="text-blue-600 hover:text-blue-800">
                                        {contactDetails.phone}
                                    </a>
                                </div>
                                <div className="md:col-span-2">
                                    <h4 className="font-semibold text-gray-800 mb-2">Address</h4>
                                    <p className="text-gray-700">{contactDetails.address}</p>
                                </div>
                            </div>
                        </section>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Privacy;